/**
 * SurveyTracers — Process tracer + patient tracer documentation page.
 * Route: /visits/:visitId/survey/tracers
 *
 * Two modes:
 * - Process Tracer: trace a system/process (e.g., Endoscope Reprocessing)
 * - Patient Tracer: follow a patient's journey through the facility (CMS/TJC methodology)
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Plus, Route, UserRound } from 'lucide-react'
import { AreaSelector } from '../../components/survey/AreaSelector'
import { TracerStepEntry } from '../../components/survey/TracerStep'
import { SyncStatus } from '../../components/survey/SyncStatus'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { surveyStore } from '../../lib/offline/surveyStore'
import { buildQueueItem } from '../../lib/services/surveySync'
import type { FacilityArea, SurveySession, TracerStep } from '../../lib/services/survey'

type TracerMode = 'process' | 'patient'

const PATIENT_TRACER_STEPS: Array<{ observation: string; standard_ref: string }> = [
  {
    observation: '',
    standard_ref: 'Pre-Operative Assessment — patient rights, H&P review, consent verification',
  },
  {
    observation: '',
    standard_ref: 'Surgical Time-Out — site verification, procedure confirmation, team briefing',
  },
  {
    observation: '',
    standard_ref: 'Intra-Operative — sterile technique, medication administration, specimen handling',
  },
  {
    observation: '',
    standard_ref: 'Post-Anesthesia Recovery — vital signs, pain assessment, discharge criteria met',
  },
  {
    observation: '',
    standard_ref: 'Discharge — discharge instructions, follow-up, medication reconciliation',
  },
]

function createPatientSteps(): TracerStep[] {
  return PATIENT_TRACER_STEPS.map((template, index) => ({
    step_id: crypto.randomUUID(),
    order: index + 1,
    observation: template.observation,
    standard_ref: template.standard_ref,
    result: 'na' as const,
    photo_ids: [],
    interview_notes: null,
    audio_transcript: null,
  }))
}

export function SurveyTracers() {
  const { visitId } = useParams<{ visitId: string }>()
  const [session, setSession] = useState<SurveySession | null>(null)
  const [area, setArea] = useState<FacilityArea | null>(null)
  const [mode, setMode] = useState<TracerMode>('process')

  // Active tracer
  const [processName, setProcessName] = useState('')
  const [patientId, setPatientId] = useState('')
  const [steps, setSteps] = useState<TracerStep[]>([])
  const [showNewTracer, setShowNewTracer] = useState(false)
  const [consentConfirmed, setConsentConfirmed] = useState(false)

  useEffect(() => {
    if (!visitId) return
    surveyStore.getSessionByVisit(visitId).then((s) => setSession(s ?? null))
  }, [visitId])

  const addStep = useCallback(() => {
    const newStep: TracerStep = {
      step_id: crypto.randomUUID(),
      order: steps.length + 1,
      observation: '',
      standard_ref: '',
      result: 'na',
      photo_ids: [],
      interview_notes: null,
      audio_transcript: null,
    }
    setSteps((prev) => [...prev, newStep])
  }, [steps.length])

  const updateStep = useCallback((index: number, updated: TracerStep) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? updated : s)))
  }, [])

  const handleStartTracer = useCallback((selectedMode: TracerMode) => {
    setMode(selectedMode)
    setShowNewTracer(true)
    if (selectedMode === 'patient') {
      setSteps(createPatientSteps())
    } else {
      setSteps([])
    }
  }, [])

  const handleSaveTracer = useCallback(async () => {
    if (!session || !visitId) return

    const tracerLabel = mode === 'patient'
      ? patientId.trim() || 'Patient Tracer'
      : processName.trim()

    if (!tracerLabel) return

    for (const step of steps) {
      const item = buildQueueItem('submit_tracer_step', visitId, session.survey_id, {
        tracer_id: '',
        tracer_type: mode,
        tracer_label: tracerLabel,
        order: step.order,
        observation: step.observation,
        standard_ref: step.standard_ref,
        result: step.result,
        photo_ids: step.photo_ids,
        interview_notes: step.interview_notes,
        audio_transcript: step.audio_transcript,
      })
      await surveyStore.enqueue(item)
    }

    // Reset
    setProcessName('')
    setPatientId('')
    setSteps([])
    setShowNewTracer(false)
    setConsentConfirmed(false)
  }, [session, visitId, processName, patientId, steps, mode])

  const handleCancel = useCallback(() => {
    setShowNewTracer(false)
    setSteps([])
    setProcessName('')
    setPatientId('')
  }, [])

  if (!visitId || !session) {
    return (
      <FieldAppShell eyebrow="Survey" title="Tracers" backTo=".." backLabel="Back">
        <div className="px-4 py-6">
          <p className="text-sm text-slate-500">No active survey.</p>
          <Link to=".." className="btn-ghost text-sm mt-4 inline-block">Back</Link>
        </div>
      </FieldAppShell>
    )
  }

  return (
    <FieldAppShell eyebrow="Survey" title="Tracers" backTo=".." backLabel="Back" rightSlot={<SyncStatus />}>
    <div className="space-y-4 px-4 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to=".." className="text-slate-400 hover:text-slate-600">
            <ArrowLeft size={20} />
          </Link>
          <h2 className="section-title">Tracers</h2>
        </div>
        <SyncStatus />
      </div>

      {!showNewTracer && (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleStartTracer('process')}
            className="btn-ghost flex flex-col items-center gap-2 py-4"
          >
            <Route size={20} className="text-[var(--brand-primary)]" />
            <span className="text-sm font-bold">Process Tracer</span>
            <span className="text-xs text-slate-500">System/process observation</span>
          </button>
          <button
            type="button"
            onClick={() => handleStartTracer('patient')}
            className="btn-ghost flex flex-col items-center gap-2 py-4"
          >
            <UserRound size={20} className="text-[var(--brand-secondary)]" />
            <span className="text-sm font-bold">Patient Tracer</span>
            <span className="text-xs text-slate-500">Follow patient journey</span>
          </button>
        </div>
      )}

      {showNewTracer && (
        <div className="space-y-4">
          <div className="app-surface p-4 space-y-3">
            <div className="flex items-center gap-2">
              {mode === 'process' ? (
                <Route size={16} className="text-[var(--brand-primary)]" />
              ) : (
                <UserRound size={16} className="text-[var(--brand-secondary)]" />
              )}
              <span className="section-title text-base">
                {mode === 'process' ? 'Process Tracer' : 'Patient Tracer'}
              </span>
            </div>

            {mode === 'process' ? (
              <input
                type="text"
                placeholder="Process name (e.g. Endoscope Reprocessing)"
                value={processName}
                onChange={(e) => setProcessName(e.target.value)}
                className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Masked patient identifier (e.g. Patient A, Case 1)"
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-secondary)] focus:outline-none focus:ring-2 focus:ring-teal-100"
                />
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">
                  Use masked identifiers only. Do not enter patient names, MRN, DOB, or any PHI.
                </p>
              </>
            )}

            <AreaSelector value={area} onChange={setArea} />

            <label className="flex items-start gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={consentConfirmed}
                onChange={(e) => setConsentConfirmed(e.target.checked)}
                className="mt-0.5 rounded border-slate-300"
              />
              Staff interview consent obtained for audio recording
            </label>
          </div>

          {/* Steps */}
          {steps.map((step, index) => (
            <TracerStepEntry
              key={step.step_id}
              step={step}
              visitId={visitId}
              surveyId={session.survey_id}
              area={area ?? 'Other'}
              onChange={(updated) => updateStep(index, updated)}
            />
          ))}

          <button
            type="button"
            onClick={addStep}
            className="btn-ghost w-full text-sm"
          >
            <Plus size={14} className="inline mr-1" /> Add Step
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSaveTracer}
              disabled={
                (mode === 'process' && !processName.trim()) ||
                steps.length === 0
              }
              className="btn-primary flex-1 py-2.5 text-sm disabled:opacity-60"
            >
              Save {mode === 'patient' ? 'Patient' : 'Process'} Tracer
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="btn-ghost flex-1 py-2.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
    </FieldAppShell>
  )
}
